import { Typography } from '@euler/app/components/typography';
import {
  ApdmReviewAction,
  apdmDraftStatusLabels,
  apdmReviewIntentLabels,
  canReviewApdmDraft,
  rpaReviewMessageLabels,
} from '@euler/functions/apdm/reviewDraft';
import { useApdmDraftReview } from '@euler/functions/apdm/useApdmDraftReview';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { apdmReplyOutputCharacterLimit } from '@euler/model/apdm';
import { formatDateTime } from '@euler/utils/formatters/datetime';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { FeedActionButton } from '../../common/FeedActionButton';
import { designRoles } from '../../common/designRoles';
import { CustomerMessageWell } from '../CustomerMessageWell';
import { NoticeLine } from '../NoticeLine';
import { OfferSection } from '../OfferSection';
import { DraftActions } from './DraftActions';
import { DraftWell } from './DraftWell';
import { senderLabel } from './senderLabel';

/**
 * Reviewing a reply draft: the recent conversation, the offer it may use,
 * why it was written, and the draft itself, to send as it is, edit and
 * send, or discard.
 */
export function ApdmDraftReview({
  storeId,
  draftId,
  onDone,
}: {
  storeId: number;
  draftId: string;
  onDone: () => void | Promise<void>;
}) {
  const { rpx } = useResponsiveContext();
  const { state, actions } = useApdmDraftReview(storeId, draftId);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(false);
  const [readSyncFailed, setReadSyncFailed] = useState(false);
  useEffect(() => {
    setText(state.detail?.draft.text ?? '');
    setEditing(false);
  }, [state.detail]);
  const submit = async (action: ApdmReviewAction) => {
    if (await actions.submit(action, text)) {
      try {
        await onDone();
      } catch {
        setReadSyncFailed(true);
      }
    }
  };
  const detail = state.detail;
  const why = detail ? detail.reason || detail.intent : null;
  return (
    <View style={{ marginTop: rpx(12) }}>
      {state.busy ? <NoticeLine text="正在处理…" /> : null}
      {state.failed ? (
        <>
          <NoticeLine
            tone="danger"
            text="未能完成操作。请刷新草稿后重新核对；刷新会重新载入草稿内容。"
          />
          <View style={{ flexDirection: 'row', marginTop: rpx(8) }}>
            <FeedActionButton
              title="刷新草稿"
              tone="secondary"
              disabled={state.busy}
              onPress={() => void actions.load()}
            />
          </View>
        </>
      ) : null}
      {detail ? (
        <>
          {/* The conversation's heading shows even with nothing in it. */}
          <View style={{ marginTop: rpx(4), gap: rpx(8) }}>
            <Typography.Text
              size={rpx(12)}
              color={designRoles.textSecondary}
              medium
            >
              近期对话
            </Typography.Text>
            {detail.messages.map(message => (
              <CustomerMessageWell
                key={message.id}
                sender={senderLabel(message.sender)}
                sentAt={message.sentAt}
                text={message.text ?? rpaReviewMessageLabels[message.type]}
              />
            ))}
          </View>
          {detail.offerText.length ? (
            <OfferSection coupon={null} texts={detail.offerText} />
          ) : (
            <View style={{ marginTop: rpx(14) }}>
              <Typography.Text
                size={rpx(12)}
                color={designRoles.textSecondary}
                medium
              >
                当前优惠
              </Typography.Text>
              <NoticeLine text="暂无可用优惠信息，请核实活动内容。" />
            </View>
          )}
          {why ? <NoticeLine text={apdmReviewIntentLabels[why]} /> : null}
          <DraftWell
            status={`${
              state.completed
                ? state.completed === ApdmReviewAction.Discard
                  ? '已丢弃'
                  : '已提交，等待发送'
                : apdmDraftStatusLabels[detail.draft.status]
            } · 到期 ${formatDateTime(detail.draft.expiresAt)}`}
            text={editing ? text : detail.draft.text}
            state={
              editing
                ? {
                    kind: 'edit',
                    disabled: state.busy || !!state.completed,
                    limit: apdmReplyOutputCharacterLimit,
                    onChangeText: setText,
                  }
                : { kind: 'read' }
            }
          />
          {state.completed ? (
            <NoticeLine
              text={
                state.completed === ApdmReviewAction.Discard
                  ? '草稿已丢弃。'
                  : '消息已提交，发送结果以实际回执为准。'
              }
            />
          ) : canReviewApdmDraft(detail.draft) ? (
            <DraftActions
              editing={editing}
              blocked={state.busy || state.failed}
              canSendEdit={
                !!text.trim() && text.length <= apdmReplyOutputCharacterLimit
              }
              onSubmit={action => void submit(action)}
              onEdit={() => setEditing(true)}
            />
          ) : (
            <NoticeLine text="此草稿已不能审核发送。" />
          )}
        </>
      ) : null}
      {readSyncFailed ? (
        <NoticeLine
          tone="danger"
          text="操作已完成，已读状态尚未同步，请刷新消息。"
        />
      ) : null}
    </View>
  );
}
